import { api, fetchServer } from "@/features/utils/fetch-server";
import { startOfDay } from "date-fns";
import type {
  CreateNumberRequestData,
  NumberRequestState,
  UpdateNumberRequestData,
} from "../../number-requests/types";
import type {
  AdminConnectorGeoFilter,
  AdminGeoFilter,
  AdminHandsetLimits,
  AdminHandsetLimitsInput,
  AdminNumberRequest,
  AdminSessionRow,
  ProvisionedNumberRequest,
  ExternalCallBucket,
  ExternalCallFailure,
  ExternalCallOperationStat,
  ExternalCallsOverview,
  ContextPressure,
  MediaHealthOverview,
  ControlCenter,
  ControlWindow,
  CostsDashboard,
  CreditProvider,
  GlobalDashboard,
  ProviderCredit,
  MetricsSnapshot,
  ProvisionInput,
  ProvisionResult,
  StaffAccess,
  StaffAccount,
  SessionSort,
  TechnicalDashboard,
  TenantOverview,
  TenantUser,
  SyntheticCallConfig,
  SyntheticCallRun,
  ToolErrorFilters,
  ToolErrorsOverview,
  ToolErrorsPage,
  DemoSpacesImportResult,
  DemoSpacesList,
} from "../types";

const base = "/api/admin/v1";

/** Fetch an authenticated CSV route as a blob, failing loudly enough to be shown. */
const downloadCsv = async (url: string, timeout?: number): Promise<Blob> => {
  const response = await fetchServer(url, { method: "GET", timeout });
  return response.blob();
};

const toolErrorParams = (hours: number, filters: ToolErrorFilters) => {
  const p = new URLSearchParams({ hours: String(hours) });
  if (filters.client) p.set("client", filters.client);
  if (filters.tool) p.set("tool", filters.tool);
  if (filters.kind) p.set("kind", filters.kind);
  if (filters.phase) p.set("phase", filters.phase);
  return p;
};

/** Cross-tenant back-office API (SYSADMIN-only on the server). */
export class AdminAPI {
  /**
   * Every tenant but the demo spaces — or, with `id`, that one tenant whatever it is (a
   * demo space is opened by its URL only).
   */
  static getTenants = async (
    days = 30,
    id?: string,
  ): Promise<TenantOverview[]> => {
    const only = id ? `&id=${encodeURIComponent(id)}` : "";
    const res = await api.get<{ tenants: TenantOverview[] }>(
      `${base}/tenants?days=${days}${only}`,
    );
    return res.tenants;
  };

  static getDashboard = async (days = 30): Promise<GlobalDashboard> =>
    api.get<GlobalDashboard>(`${base}/dashboard?days=${days}`);

  /**
   * The control centre: live conversations, health, traffic, geography, incidents.
   * "today" sends the browser's midnight, read at each fetch so a screen left open
   * overnight rolls over to the new day on its own. `tz` is the zone the peak hours are
   * read in — the screen's own, like "today".
   */
  static getControlCenter = async (
    span: ControlWindow = 24,
  ): Promise<ControlCenter> =>
    api.get<ControlCenter>(
      `${base}/control-center?${
        span === "today"
          ? `since=${startOfDay(new Date()).getTime()}`
          : `hours=${span}`
      }&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`,
    );

  static getTechnical = async (days = 30): Promise<TechnicalDashboard> =>
    api.get<TechnicalDashboard>(`${base}/technical?days=${days}`);

  /** What everything cost over the window: per minute of call, phase, model, tenant. */
  static getCosts = async (days = 30): Promise<CostsDashboard> =>
    api.get<CostsDashboard>(`${base}/costs?days=${days}`);

  /** Hourly technical series over the last `hours` (default 7 days, server max 90 days). */
  static getTimeseries = async (hours = 24 * 7): Promise<MetricsSnapshot[]> => {
    const res = await api.get<{ snapshots: MetricsSnapshot[] }>(
      `${base}/technical/timeseries?hours=${hours}`,
    );
    return res.snapshots;
  };

  /** Recompute the hourly series over a window (seed history, or repair after an incident). */
  static backfillTimeseries = async (
    hours: number,
  ): Promise<{ buckets: number; from: number; to: number }> =>
    api.post<{ buckets: number; from: number; to: number }, { hours: number }>(
      `${base}/technical/timeseries/backfill`,
      { hours },
    );

  static getSessions = async (opts: {
    days?: number;
    sort?: SessionSort;
    order?: "asc" | "desc";
    fallbackUsed?: boolean;
    limit?: number;
  }): Promise<AdminSessionRow[]> => {
    const p = new URLSearchParams();
    if (opts.days) p.set("days", String(opts.days));
    if (opts.sort) p.set("sort", opts.sort);
    if (opts.order) p.set("order", opts.order);
    if (opts.fallbackUsed) p.set("fallback", "used");
    if (opts.limit) p.set("limit", String(opts.limit));
    const res = await api.get<{ sessions: AdminSessionRow[] }>(
      `${base}/sessions?${p.toString()}`,
    );
    return res.sessions;
  };

  /** Credit state of the upstream vendors. `refresh` forces a probe instead of the cache. */
  static getCredits = async (refresh = false): Promise<ProviderCredit[]> => {
    const res = await api.get<{ credits: ProviderCredit[] }>(
      `${base}/credits${refresh ? "?refresh=1" : ""}`,
    );
    return res.credits;
  };

  /**
   * Set a provider's manual balance and/or alert threshold. `balance: null` clears the
   * manual entry; `threshold: 0` falls back to the global default.
   */
  static updateCredit = async (
    provider: CreditProvider,
    input: { balance?: number | null; threshold?: number | null },
  ): Promise<ProviderCredit> => {
    const res = await api.put<
      { credit: ProviderCredit },
      { balance?: number | null; threshold?: number | null }
    >(`${base}/credits/${provider}`, input);
    return res.credit;
  };

  /** Run the alerting pass now (probe + Sentry), to check the wiring end to end. */
  static checkCredits = async (): Promise<ProviderCredit[]> => {
    const res = await api.post<{ credits: ProviderCredit[] }, unknown>(
      `${base}/credits/check`,
      {},
    );
    return res.credits;
  };

  // ------------------------------------------------------ context pressure

  /** How full the talking models' context window gets, estimated per turn. */
  static getContextPressure = async (
    hours = 24,
    filters: { client?: string } = {},
  ): Promise<ContextPressure> => {
    const p = new URLSearchParams({ hours: String(hours) });
    if (filters.client) p.set("client", filters.client);
    return api.get<ContextPressure>(`${base}/context-pressure?${p}`);
  };

  // ------------------------------------------------------------ media health

  /** Did the callers' audio reach us: per-call verdicts, degraded calls only unless `all`. */
  static getMediaHealth = async (
    hours = 24,
    all = false,
  ): Promise<MediaHealthOverview> => {
    const p = new URLSearchParams({ hours: String(hours) });
    if (all) p.set("all", "1");
    return api.get<MediaHealthOverview>(`${base}/media-health?${p}`);
  };

  // ------------------------------------------------------ external API calls

  /**
   * Outbound third-party traffic over the last `hours`, grouped by integration and by
   * target (host for the HTTP adapters, domain for the browser agent).
   */
  static getExternalCalls = async (
    hours = 24,
    filters: { group?: string; client?: string } = {},
  ): Promise<ExternalCallsOverview> => {
    const p = new URLSearchParams({ hours: String(hours) });
    if (filters.group) p.set("group", filters.group);
    if (filters.client) p.set("client", filters.client);
    return api.get<ExternalCallsOverview>(`${base}/external-calls?${p}`);
  };

  /** Per-endpoint breakdown inside one integration (optionally one target). */
  static getExternalCallOperations = async (
    hours = 24,
    filters: { group?: string; target?: string } = {},
  ): Promise<ExternalCallOperationStat[]> => {
    const p = new URLSearchParams({ hours: String(hours) });
    if (filters.group) p.set("group", filters.group);
    if (filters.target) p.set("target", filters.target);
    const res = await api.get<{ operations: ExternalCallOperationStat[] }>(
      `${base}/external-calls/operations?${p}`,
    );
    return res.operations;
  };

  /** Hourly volume/error/latency series. Targets are broken out when a group is given. */
  static getExternalCallsTimeseries = async (
    hours = 24 * 7,
    filters: { group?: string; target?: string } = {},
  ): Promise<ExternalCallBucket[]> => {
    const p = new URLSearchParams({ hours: String(hours) });
    if (filters.group) p.set("group", filters.group);
    if (filters.target) p.set("target", filters.target);
    const res = await api.get<{ buckets: ExternalCallBucket[] }>(
      `${base}/external-calls/timeseries?${p}`,
    );
    return res.buckets;
  };

  /** The failures themselves, newest first. */
  static getExternalCallFailures = async (
    hours = 24,
    filters: { group?: string; target?: string; limit?: number } = {},
  ): Promise<ExternalCallFailure[]> => {
    const p = new URLSearchParams({ hours: String(hours) });
    if (filters.group) p.set("group", filters.group);
    if (filters.target) p.set("target", filters.target);
    if (filters.limit) p.set("limit", String(filters.limit));
    const res = await api.get<{ failures: ExternalCallFailure[] }>(
      `${base}/external-calls/errors?${p}`,
    );
    return res.failures;
  };

  // ------------------------------------------------------------- tool errors

  /** Per-tool rollup of the tool calls that came back in error over the last `hours`. */
  static getToolErrors = async (
    hours = 24 * 7,
    filters: ToolErrorFilters = {},
  ): Promise<ToolErrorsOverview> =>
    api.get<ToolErrorsOverview>(
      `${base}/tool-errors?${toolErrorParams(hours, filters)}`,
    );

  /** The error rows themselves, newest first (server max 200 per page). */
  static listToolErrors = async (
    hours = 24 * 7,
    filters: ToolErrorFilters = {},
    page: { limit?: number; offset?: number } = {},
  ): Promise<ToolErrorsPage> => {
    const p = toolErrorParams(hours, filters);
    if (page.limit) p.set("limit", String(page.limit));
    if (page.offset) p.set("offset", String(page.offset));
    return api.get<ToolErrorsPage>(`${base}/tool-errors/list?${p}`);
  };

  // ---------------------------------------------------------- number requests

  /**
   * Every tenant's number requests, already ordered by what needs working first
   * (waiting on us, then on the carrier, then on the client; oldest first
   * within each). `states` narrows the queue; absent means all of them.
   */
  static getNumberRequests = async (
    filters: { states?: NumberRequestState[]; client?: string } = {},
  ): Promise<AdminNumberRequest[]> => {
    const p = new URLSearchParams();
    for (const state of filters.states || []) p.append("state", state);
    if (filters.client) p.set("client", filters.client);
    const res = await api.get<{ requests: AdminNumberRequest[] }>(
      `${base}/number-requests${p.size ? `?${p}` : ""}`,
    );
    return res.requests;
  };

  static getNumberRequest = async (
    clientId: string,
    requestId: string,
  ): Promise<AdminNumberRequest> => {
    const res = await api.get<{ request: AdminNumberRequest }>(
      `${base}/number-requests/${clientId}/${requestId}`,
    );
    return res.request;
  };

  /**
   * Open a request for a client we are setting up ourselves — one who ordered a
   * number over the phone and is not going to fill a form so that we can then
   * fulfil it. The request belongs to the tenant, who sees it on their own page.
   */
  static createNumberRequest = async (
    clientId: string,
    data: CreateNumberRequestData,
  ): Promise<AdminNumberRequest> => {
    const res = await api.post<
      { request: AdminNumberRequest },
      CreateNumberRequestData
    >(`${base}/number-requests/${clientId}`, data);
    return res.request;
  };

  /** Fill in what we learn before the client does — the provisioned number. */
  static updateNumberRequest = async (
    clientId: string,
    requestId: string,
    data: UpdateNumberRequestData,
  ): Promise<AdminNumberRequest> => {
    const res = await api.put<
      { request: AdminNumberRequest },
      UpdateNumberRequestData
    >(`${base}/number-requests/${clientId}/${requestId}`, data);
    return res.request;
  };

  /** Advance the approval state machine. Refused server-side if illegal. */
  static transitionNumberRequest = async (
    clientId: string,
    requestId: string,
    state: NumberRequestState,
    comment?: string,
  ): Promise<AdminNumberRequest> => {
    const res = await api.post<
      { request: AdminNumberRequest },
      { state: NumberRequestState; comment?: string }
    >(`${base}/number-requests/${clientId}/${requestId}/transition`, {
      state,
      comment,
    });
    return res.request;
  };

  /**
   * Validate the file and hand the line over in one move: the number is saved
   * on the request, a HeyUp channel is created (or extended) with it, and the
   * request lands on `provisioned`. Server-side because the three steps have to
   * happen together — a number saved on a request nobody connected answers
   * nothing.
   */
  static provisionNumberRequest = async (
    clientId: string,
    requestId: string,
    data: { phone_number?: string; comment?: string },
  ): Promise<ProvisionedNumberRequest> => {
    const res = await api.post<
      { request: ProvisionedNumberRequest },
      { phone_number?: string; comment?: string }
    >(`${base}/number-requests/${clientId}/${requestId}/provision`, data);
    return res.request;
  };

  /**
   * Record a parcel shipped to the client. The server e-mails them the tracking
   * link as it saves it — the request's own address, else the owners.
   */
  static addNumberRequestDelivery = async (
    clientId: string,
    requestId: string,
    data: { tracking_url: string; label?: string },
  ): Promise<AdminNumberRequest> => {
    const res = await api.post<
      { request: AdminNumberRequest },
      { tracking_url: string; label?: string }
    >(`${base}/number-requests/${clientId}/${requestId}/deliveries`, data);
    return res.request;
  };

  /** Take a parcel off the request. Sends nothing. */
  static removeNumberRequestDelivery = async (
    clientId: string,
    requestId: string,
    deliveryId: string,
  ): Promise<AdminNumberRequest> => {
    const res = await api.delete<{ request: AdminNumberRequest }>(
      `${base}/number-requests/${clientId}/${requestId}/deliveries/${deliveryId}`,
    );
    return res.request;
  };

  /**
   * Fetch a supporting document as a blob. The route is authenticated, so the
   * file cannot simply be linked to — it comes back here and is handed to the
   * browser as an object URL.
   */
  static downloadNumberRequestDocument = async (
    clientId: string,
    requestId: string,
    type: string,
  ): Promise<Blob> => {
    const response = await fetchServer(
      `${base}/number-requests/${clientId}/${requestId}/documents/${type}/download`,
      { method: "GET" },
    );

    return response.blob();
  };

  /** Create a user with a chosen email/password, in a new or existing tenant. */
  // ── Telephony limits ──────────────────────────────────────────────────────
  //
  // The tenant-facing routes refuse to write these fields, and platform staff
  // cannot reach those routes anyway (they require an ACTIVE membership of the
  // tenant). These are the way in.

  static getHandsetLimits = async (): Promise<AdminHandsetLimits[]> => {
    const res = await api.get<{ handsets: AdminHandsetLimits[] }>(
      `${base}/telephony/handsets`,
    );
    return res.handsets || [];
  };

  static setHandsetLimits = async (
    clientId: string,
    deviceId: string,
    input: AdminHandsetLimitsInput,
  ): Promise<void> => {
    await api.put(`${base}/telephony/handsets/${clientId}/${deviceId}`, input);
  };

  static getConnectorGeoFilters = async (): Promise<
    AdminConnectorGeoFilter[]
  > => {
    const res = await api.get<{ connectors: AdminConnectorGeoFilter[] }>(
      `${base}/telephony/connectors`,
    );
    return res.connectors || [];
  };

  static setConnectorGeoFilter = async (
    clientId: string,
    connectorId: string,
    geoFilter: AdminGeoFilter,
  ): Promise<void> => {
    await api.put(
      `${base}/telephony/connectors/${clientId}/${connectorId}/geo-filter`,
      { geo_filter: geoFilter },
    );
  };

  /** Who reaches this back-office, and how far (SYSADMIN only). */
  static getStaffAccounts = async (): Promise<StaffAccount[]> => {
    const res = await api.get<{ accounts: StaffAccount[] }>(
      `${base}/staff-access`,
    );
    return res.accounts;
  };

  /**
   * Give an existing account a back-office access, by its e-mail — `admin`,
   * `read` (sales), or `none` to take it away. Answers the list as it now stands.
   */
  static setStaffAccess = async (
    email: string,
    access: StaffAccess | "none",
  ): Promise<StaffAccount[]> => {
    const res = await api.put<
      { accounts: StaffAccount[] },
      { email: string; access: StaffAccess | "none" }
    >(`${base}/staff-access`, { email, access });
    return res.accounts;
  };

  static provision = async (input: ProvisionInput): Promise<ProvisionResult> =>
    api.post<ProvisionResult, ProvisionInput>(`${base}/provision`, input);

  static getTenantUsers = async (tenantId: string): Promise<TenantUser[]> => {
    const res = await api.get<{ users: TenantUser[] }>(
      `${base}/tenants/${tenantId}/users`,
    );
    return res.users;
  };

  /** Revoke a member's access to a tenant (the user account itself is kept). */
  static removeTenantUser = async (
    tenantId: string,
    userId: string,
  ): Promise<{ success: boolean }> =>
    api.delete<{ success: boolean }>(
      `${base}/tenants/${tenantId}/users/${userId}`,
    );

  static resetPassword = async (
    userId: string,
    password?: string,
  ): Promise<{ email: string; password: string }> =>
    api.post<{ email: string; password: string }, { password?: string }>(
      `${base}/users/${userId}/password`,
      { password },
    );

  // ------------------------------------------------------- synthetic calls
  /** History of the daily end-to-end booking test, newest first. */
  static getSyntheticCalls = async (
    options: { limit?: number; status?: string } = {},
  ): Promise<{
    runs: SyntheticCallRun[];
    config: SyntheticCallConfig;
    running: boolean;
  }> => {
    const params = new URLSearchParams();
    if (options.limit) params.set("limit", String(options.limit));
    if (options.status) params.set("status", options.status);
    return api.get<{
      runs: SyntheticCallRun[];
      config: SyntheticCallConfig;
      running: boolean;
    }>(`${base}/synthetic-calls?${params.toString()}`);
  };

  /** One run, with its transcript and what the robot heard. */
  static getSyntheticCall = async (id: string): Promise<SyntheticCallRun> => {
    const res = await api.get<{ run: SyntheticCallRun }>(
      `${base}/synthetic-calls/${id}`,
    );
    return res.run;
  };

  /**
   * The test-call history as a CSV, to be read outside the page. The route is authenticated,
   * so it cannot simply be linked to: it comes back here and is handed to the browser.
   */
  static getSyntheticCallsCsv = async (
    options: { limit?: number; status?: string } = {},
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    if (options.limit) params.set("limit", String(options.limit));
    if (options.status) params.set("status", options.status);
    return downloadCsv(`${base}/synthetic-calls/export.csv?${params}`);
  };

  /**
   * Every conversation of one tenant started in `[from, to)` (epoch ms), as a CSV: one row
   * per conversation, or every message of each.
   */
  static getTenantSessionsCsv = async (
    tenantId: string,
    options: { from: number; to: number; format: "sessions" | "messages" },
  ): Promise<Blob> => {
    const params = new URLSearchParams({
      from: String(options.from),
      to: String(options.to),
      format: options.format,
    });
    return downloadCsv(
      `${base}/tenants/${encodeURIComponent(tenantId)}/sessions/export.csv?${params}`,
      // A year of a busy tenant's messages is built server-side in one go: well past the
      // 30s default.
      5 * 60 * 1000,
    );
  };

  /** One run as a CSV: summary, checks, verdict, conversation, what the robot heard. */
  static getSyntheticCallCsv = async (id: string): Promise<Blob> =>
    downloadCsv(`${base}/synthetic-calls/${id}/export.csv`);

  /**
   * Start the test call now. Answers immediately — the call itself lasts minutes, so the
   * page watches the list for the row it eventually produces.
   */
  static runSyntheticCall = async (): Promise<{ started: boolean }> =>
    api.post<{ started: boolean }, unknown>(`${base}/synthetic-calls/run`, {});

  // ----------------------------------------------------------------- demo spaces

  static getDemoSpaces = async (): Promise<DemoSpacesList> =>
    api.get<DemoSpacesList>(`${base}/demo-spaces`);

  /** The CSV to fill in: the columns an import reads, with one example line. */
  static getDemoSpacesTemplateCsv = async (): Promise<Blob> =>
    downloadCsv(`${base}/demo-spaces/template.csv`);

  /** Every demo tenant with its number and code, for the mailing. */
  static getDemoSpacesCsv = async (): Promise<Blob> =>
    downloadCsv(`${base}/demo-spaces/export.csv`);

  /** Every demo tenant with what was read for it (services, team) and its gaps. */
  static getDemoSpacesJson = async (): Promise<Blob> =>
    downloadCsv(`${base}/demo-spaces/export.json`, 5 * 60 * 1000);

  static importDemoSpaces = async (
    csv: string,
  ): Promise<DemoSpacesImportResult> =>
    api.post<DemoSpacesImportResult, { csv: string }>(
      `${base}/demo-spaces/import`,
      { csv },
    );

  static retryDemoSpace = async (id: string): Promise<{ success: boolean }> =>
    api.post<{ success: boolean }, unknown>(
      `${base}/demo-spaces/${encodeURIComponent(id)}/retry`,
      {},
    );
}
